import { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Loader2, CopyX, Wand2, CheckCircle2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const getAuthHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token')}` });

export const DuplicatesDialog = ({ open, onOpenChange, onMerged }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const scan = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/retreat/duplicates`, { headers: getAuthHeaders() });
      setData(res.data);
    } catch {
      toast.error('Failed to scan for duplicates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (open) scan(); }, [open, scan]);

  const autoFix = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/retreat/duplicates/auto`, {}, { headers: getAuthHeaders() });
      toast.success(`${res.data.merged.length} duplicate(s) merged automatically`);
      await scan();
      onMerged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Auto-fix failed');
    } finally {
      setBusy(false);
    }
  };

  const keepThis = async (group, keepId) => {
    setBusy(true);
    try {
      for (const rec of group.records) {
        if (rec.id !== keepId) {
          await axios.post(`${BACKEND_URL}/api/retreat/duplicates/merge`,
            { keep_id: keepId, remove_id: rec.id }, { headers: getAuthHeaders() });
        }
      }
      toast.success('Merged — payments, room, and login carried over');
      await scan();
      onMerged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Merge failed');
    } finally {
      setBusy(false);
    }
  };

  const groups = data ? [...data.email_groups, ...data.name_groups] : [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="duplicates-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CopyX className="w-4 h-4 text-orange-500" /> Duplicate Check
          </DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 text-gray-300 animate-spin" /></div>
        ) : !data ? null : groups.length === 0 ? (
          <div className="py-10 text-center" data-testid="duplicates-clean">
            <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-emerald-400" />
            <p className="text-sm text-gray-600 font-medium">No duplicates found — the list is clean</p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-600">{groups.length} possible duplicate group(s). Pick which record to keep — payments, room, and login are merged in automatically. Registered always beats waitlisted.</p>
              <Button size="sm" onClick={autoFix} disabled={busy} className="shrink-0 ml-3 bg-gradient-to-r from-wine to-burgundy" data-testid="auto-fix-duplicates-btn">
                {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5 mr-1.5" />}
                Auto-fix
              </Button>
            </div>
            <div className="max-h-96 overflow-y-auto space-y-3">
              {groups.map((g) => (
                <div key={`${g.type}-${g.key}`} className="rounded-lg border border-amber-200 bg-amber-50/40 p-3">
                  <p className="text-xs font-semibold text-amber-800 uppercase tracking-wide mb-2">
                    {g.type === 'same_email' ? `Same email: ${g.key}` : `Same name: ${g.key}`}
                  </p>
                  <div className="space-y-2">
                    {g.records.map((r) => (
                      <div key={r.id} className="flex flex-wrap items-center justify-between gap-2 bg-white rounded-md border border-gray-100 p-2.5">
                        <div className="text-sm">
                          <span className="font-medium text-gray-900">{r.name}</span>{' '}
                          <span className="text-gray-500 text-xs">{r.email}</span>{' '}
                          <Badge variant="outline" className="text-[10px] ml-1">{r.ticket_status}</Badge>
                          {r.amount_paid ? <span className="text-xs text-emerald-700 ml-1">${r.amount_paid}</span> : null}
                        </div>
                        <Button size="sm" variant="outline" disabled={busy} onClick={() => keepThis(g, r.id)} data-testid={`keep-record-${r.id}`}>
                          Keep this one
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
